import { useRef, useCallback } from "react";
import type { QuizFormData } from "@core/features/quiz";
import { FitQuizLayout } from "./FitQuizLayout";
import { useGetAnonymousQuiz } from "@platform/features/programQuiz/components/AnonymousQuiz/useGetAnonymousQuiz";
import { useAnonymousQuizNavigation } from "@platform/features/programQuiz/components/AnonymousQuiz/useAnonymousQuizNavigation";
import { QuizQuestionContent, type QuizQuestionContentRef } from "./QuizQuestionContent";

type Props = {
  /** The quiz ID to fetch and display */
  quizId: number;
  /** Callback when quiz is completed with all collected answers */
  onComplete: (formData: QuizFormData) => void;
};

/**
 * Container component that combines FitQuizLayout with anonymous quiz functionality.
 * Handles data fetching, navigation state, and quiz completion.
 */
export function FitQuizLayoutContainer({
  quizId,
  onComplete,
}: Props) {
  const quizContentRef = useRef<QuizQuestionContentRef>(null);

  const { data: quiz, isLoading, error } = useGetAnonymousQuiz({ quizId });

  const {
    activeQuestionSet,
    buttonLabel,
    filterQuestionSet,
    handleNextQuestionObject,
    handlePreviousQuestionObject,
    isSubmitting,
    formData,
    stepIndex,
    totalSteps,
  } = useAnonymousQuizNavigation({
    quiz,
    onCompleteQuiz: onComplete,
  });

  const handleButtonClick = useCallback(async () => {
    if (quizContentRef.current) {
      await quizContentRef.current.submit();
    }
  }, []);

  const isQuizLoading = isLoading || !!error || !quiz;

  return (
    <FitQuizLayout
      title="Is Pollinator right for you?"
      description="Please confirm a few details to make sure Pollinator is a good fit for you."
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      buttonLabel={buttonLabel}
      isButtonDisabled={isSubmitting}
      onButtonClick={handleButtonClick}
      showBackButton={stepIndex > 0}
      onBackClick={handlePreviousQuestionObject}
      isLoading={isQuizLoading}
    >
      <QuizQuestionContent
        ref={quizContentRef}
        activeQuestionSet={activeQuestionSet}
        filterQuestionSet={filterQuestionSet}
        onSubmit={handleNextQuestionObject}
        formData={formData}
        stepIndex={stepIndex}
      />
    </FitQuizLayout>
  );
}
